<script setup lang="ts">
  defineOptions({ name: 'CPBreadcrumb', inheritAttrs: false })

  import { useAttrs, type PropType } from 'vue'

  import { HLBreadcrumb } from '@platform-ui/highrise'

  import type { CPBreadcrumbItem } from './types/breadcrumb.types'

  const props = defineProps({
    /**
     * Stable DOM id for the breadcrumb root (a11y / testing).
     */
    id: {
      type: String,
      default: undefined,
    },
    /**
     * Trail segments — same shape as HighRise `HLBreadcrumb` `breadcrumbs`.
     */
    breadcrumbs: {
      type: Array as PropType<CPBreadcrumbItem[]>,
      required: true,
    },
    separator: {
      type: String,
      default: undefined,
    },
    size: {
      type: String as PropType<'sm' | 'md' | 'lg'>,
      default: undefined,
    },
    home: {
      type: Boolean,
      default: undefined,
    },
    homeHref: {
      type: String,
      default: undefined,
    },
  })

  const emit = defineEmits<{
    homeClick: [label: string]
  }>()

  const attrs = useAttrs()

  function handleHomeClick(label: string) {
    emit('homeClick', label)
    const fromAttrs = attrs.onHomeClick as ((l: string) => void) | undefined
    if (typeof fromAttrs === 'function') {
      fromAttrs(label)
    }
  }
</script>

<template>
  <HLBreadcrumb
    v-bind="$attrs"
    :id="props.id"
    :breadcrumbs="props.breadcrumbs"
    :separator="props.separator"
    :size="props.size"
    :home="props.home"
    :home-href="props.homeHref"
    :on-home-click="handleHomeClick"
  >
    <template v-for="(_, name) in $slots" :key="name" #[name]="slotProps">
      <slot :name="name" v-bind="slotProps" />
    </template>
  </HLBreadcrumb>
</template>

<style>
  .hr-breadcrumb .hr-breadcrumb-item__link {
    --n-item-text-color: var(--cp-font-600);
    --n-item-text-color-active: var(--cp-font-800);
  }

  .hr-breadcrumb .hr-breadcrumb-item__link:hover {
    --n-item-text-color: var(--cp-font-800);
  }

  .hr-breadcrumb .hr-breadcrumb-item__separator {
    --n-separator-color: var(--cp-font-600);
  }
</style>
